<template>
  <div v-if="open && routeAllowsFloat" class="leo-admin-float-root">
    <button
      v-show="minimized"
      type="button"
      class="leo-admin-float-pill"
      :style="pillStyle"
      @click="expand"
    >
      Leo
    </button>
    <div
      v-show="!minimized"
      ref="panel"
      class="leo-admin-float-panel"
      :style="panelStyle"
    >
      <header
        class="leo-admin-float-head"
        @mousedown.prevent="onDragStart"
      >
        <span class="leo-admin-float-title">{{ $t('route.leoFloatDockTitle') }}</span>
        <div class="leo-admin-float-actions">
          <button
            type="button"
            class="leo-admin-float-iconbtn"
            :title="$t('route.leoFloatDockMinimize')"
            aria-label="minimize"
            @click="minimize"
          >
            −
          </button>
          <button
            type="button"
            class="leo-admin-float-iconbtn leo-admin-float-iconbtn--close"
            :title="$t('route.leoFloatDockClose')"
            aria-label="close"
            @click="close"
          >
            ×
          </button>
        </div>
      </header>
      <iframe
        class="leo-admin-float-iframe"
        :src="frameSrc"
        title="Leo chat"
      />
      <div
        class="leo-admin-float-resize"
        :title="$t('route.leoFloatDockResizeHint')"
        aria-hidden="true"
        @mousedown.stop.prevent="onResizeStart"
      />
    </div>
  </div>
</template>

<script>
import {
  leoFloatDockBus,
  getLeoFloatDockOpen,
  getLeoFloatDockMinimized,
  setLeoFloatDockOpen,
  setLeoFloatDockMinimized,
  getLeoFloatDockPosition,
  setLeoFloatDockPosition,
  getLeoFloatDockPanelSize,
  setLeoFloatDockPanelSize
} from '@/utils/leoAdminFloatDock'

const DEFAULT_W = 400
const DEFAULT_H = 520
const MIN_W = 280
const MIN_H = 300
const VIEW_PAD = 8

export default {
  name: 'LeoAdminFloatDock',
  data () {
    return {
      open: false,
      minimized: false,
      pos: null,
      panelW: DEFAULT_W,
      panelH: DEFAULT_H,
      dragging: false,
      dragDx: 0,
      dragDy: 0,
      resizing: false,
      resizeStartX: 0,
      resizeStartY: 0,
      resizeStartW: 0,
      resizeStartH: 0,
      resizeAnchor: null
    }
  },
  computed: {
    routeAllowsFloat () {
      const n = this.$route.name
      const p = this.$route.path || ''
      if (n === 'login' || p === '/login') return false
      const hideNames = ['leoStandaloneChat', 'leoConsole', 'leoPortal', 'leoLogin', 'leoMyPreferences']
      if (hideNames.indexOf(n) !== -1) return false
      if (p.indexOf('/leo/') === 0 || p === '/leo-portal') return false
      return true
    },
    frameSrc () {
      const r = this.$router.resolve({
        name: 'leoStandaloneChat',
        query: { surface: 'soc_admin' }
      })
      try {
        return new URL(r.href, window.location.origin).href
      } catch (e) {
        return r.href
      }
    },
    panelStyle () {
      const size = {
        width: `${this.panelW}px`,
        height: `${this.panelH}px`
      }
      if (this.pos && typeof this.pos.left === 'number' && typeof this.pos.top === 'number') {
        return {
          ...size,
          left: `${this.pos.left}px`,
          top: `${this.pos.top}px`,
          right: 'auto',
          bottom: 'auto'
        }
      }
      return {
        ...size,
        right: '24px',
        bottom: '24px',
        left: 'auto',
        top: 'auto'
      }
    },
    pillStyle () {
      if (this.pos && typeof this.pos.left === 'number') {
        return {
          left: `${Math.max(8, this.pos.left)}px`,
          bottom: '24px'
        }
      }
      return { right: '24px', bottom: '24px' }
    }
  },
  created () {
    this.syncFromStore()
    leoFloatDockBus.$on('sync', this.syncFromStore)
  },
  beforeDestroy () {
    leoFloatDockBus.$off('sync', this.syncFromStore)
    window.removeEventListener('mousemove', this.onDragMove)
    window.removeEventListener('mouseup', this.onDragEnd)
    window.removeEventListener('mousemove', this.onResizeMove)
    window.removeEventListener('mouseup', this.onResizeEnd)
  },
  methods: {
    syncFromStore () {
      this.open = getLeoFloatDockOpen()
      this.minimized = getLeoFloatDockMinimized()
      this.pos = getLeoFloatDockPosition()
      const sz = getLeoFloatDockPanelSize()
      if (sz) {
        const vw = typeof window !== 'undefined' ? window.innerWidth : 1920
        const vh = typeof window !== 'undefined' ? window.innerHeight : 1080
        let w = Math.round(sz.width)
        let h = Math.round(sz.height)
        w = Math.min(Math.max(MIN_W, w), vw - 2 * VIEW_PAD)
        h = Math.min(Math.max(MIN_H, h), vh - 2 * VIEW_PAD)
        this.panelW = w
        this.panelH = h
      } else {
        this.panelW = DEFAULT_W
        this.panelH = DEFAULT_H
      }
    },
    close () {
      setLeoFloatDockOpen(false)
    },
    minimize () {
      setLeoFloatDockMinimized(true)
    },
    expand () {
      setLeoFloatDockMinimized(false)
    },
    onDragStart (e) {
      if (e.button !== 0) return
      const panel = this.$refs.panel
      if (!panel) return
      const rect = panel.getBoundingClientRect()
      this.dragging = true
      this.dragDx = e.clientX - rect.left
      this.dragDy = e.clientY - rect.top
      if (!this.pos) {
        this.pos = { left: rect.left, top: rect.top }
      }
      window.addEventListener('mousemove', this.onDragMove)
      window.addEventListener('mouseup', this.onDragEnd)
    },
    onDragMove (e) {
      if (!this.dragging) return
      let left = e.clientX - this.dragDx
      let top = e.clientY - this.dragDy
      const vw = window.innerWidth
      const vh = window.innerHeight
      left = Math.min(Math.max(VIEW_PAD, left), vw - this.panelW - VIEW_PAD)
      top = Math.min(Math.max(VIEW_PAD, top), vh - this.panelH - VIEW_PAD)
      this.pos = { left, top }
    },
    onDragEnd () {
      if (!this.dragging) return
      this.dragging = false
      window.removeEventListener('mousemove', this.onDragMove)
      window.removeEventListener('mouseup', this.onDragEnd)
      if (this.pos) {
        setLeoFloatDockPosition(this.pos.left, this.pos.top)
        setLeoFloatDockPanelSize(this.panelW, this.panelH)
      }
    },
    onResizeStart (e) {
      if (e.button !== 0) return
      const panel = this.$refs.panel
      if (!panel) return
      const rect = panel.getBoundingClientRect()
      this.resizing = true
      this.resizeStartX = e.clientX
      this.resizeStartY = e.clientY
      this.resizeStartW = rect.width
      this.resizeStartH = rect.height
      this.resizeAnchor = { left: rect.left, top: rect.top }
      window.addEventListener('mousemove', this.onResizeMove)
      window.addEventListener('mouseup', this.onResizeEnd)
    },
    onResizeMove (e) {
      if (!this.resizing || !this.resizeAnchor) return
      const vw = window.innerWidth
      const vh = window.innerHeight
      const maxW = vw - this.resizeAnchor.left - VIEW_PAD
      const maxH = vh - this.resizeAnchor.top - VIEW_PAD
      const dw = e.clientX - this.resizeStartX
      const dh = e.clientY - this.resizeStartY
      let w = this.resizeStartW + dw
      let h = this.resizeStartH + dh
      w = Math.min(Math.max(MIN_W, w), Math.max(MIN_W, maxW))
      h = Math.min(Math.max(MIN_H, h), Math.max(MIN_H, maxH))
      this.panelW = Math.round(w)
      this.panelH = Math.round(h)
    },
    onResizeEnd () {
      if (!this.resizing) return
      this.resizing = false
      this.resizeAnchor = null
      window.removeEventListener('mousemove', this.onResizeMove)
      window.removeEventListener('mouseup', this.onResizeEnd)
      setLeoFloatDockPanelSize(this.panelW, this.panelH)
    }
  }
}
</script>

<style scoped>
.leo-admin-float-root {
  position: relative;
  z-index: 0;
}

.leo-admin-float-panel {
  position: fixed;
  z-index: 100050;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.28);
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  min-width: 280px;
  min-height: 300px;
}

.leo-admin-float-head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  background: linear-gradient(90deg, #1a1a2e 0%, #16213e 100%);
  color: #fff;
  cursor: grab;
  user-select: none;
}

.leo-admin-float-head:active {
  cursor: grabbing;
}

.leo-admin-float-title {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.leo-admin-float-actions {
  display: flex;
  gap: 4px;
}

.leo-admin-float-iconbtn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}

.leo-admin-float-iconbtn:hover {
  background: rgba(255, 255, 255, 0.22);
}

.leo-admin-float-iconbtn--close:hover {
  background: rgba(220, 53, 69, 0.85);
}

.leo-admin-float-iframe {
  flex: 1;
  width: 100%;
  min-height: 0;
  border: 0;
  background: #f5f6fa;
}

.leo-admin-float-resize {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 18px;
  height: 18px;
  cursor: nwse-resize;
  z-index: 2;
  background: linear-gradient(
    135deg,
    transparent 0%,
    transparent 50%,
    rgba(255, 255, 255, 0.25) 50%,
    rgba(255, 255, 255, 0.45) 100%
  );
}

.leo-admin-float-resize:hover {
  background: linear-gradient(
    135deg,
    transparent 0%,
    transparent 45%,
    rgba(100, 180, 255, 0.35) 45%,
    rgba(100, 180, 255, 0.55) 100%
  );
}

.leo-admin-float-pill {
  position: fixed;
  z-index: 100050;
  padding: 10px 18px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #1a1a2e, #0f3460);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
}

.leo-admin-float-pill:hover {
  filter: brightness(1.08);
}
</style>



// WEBPACK FOOTER //
// src/components/LeoAdmin/LeoAdminFloatDock.vue